import React, { useEffect, useMemo, useRef, useState } from "react"

import { useNavigate } from "react-router-dom"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { CheckboxElement } from "@front-shared/elements/checkbox.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { POLICIES_COLUMNS_CONFIG } from "../../../config/policiesColumns.config"
import { ExportReportHelper } from "../../../helpers/exportReport.helper"
import { PoliciesHelper } from "../../../helpers/policies.helper"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"

import forwardIcon from "@front-shared/assets/images/icons/forward.svg"

export const PoliciesListRowComponent: React.FunctionComponent<
    IPoliciesListRowComponentProps
> = props => {
    const navigate = useNavigate()

    const isOverridesEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_OVERRIDES,
    )

    const localContainerRef = useRef<HTMLDivElement>(null)

    const containerRef = useMemo(() => {
        return props.elementRef || localContainerRef
    }, [props.elementRef, localContainerRef])

    const [isRowHovered, setIsRowHovered] = useState<boolean>(false)
    const [actionsContainerMarginLeft, setActionsContainerMarginLeft] =
        useState<number>(0)

    useEffect(() => {
        if (!containerRef.current || !isRowHovered) {
            return
        }

        window.addEventListener("scroll", adjustActionsContainerMarginLeft)

        return () => {
            window.removeEventListener(
                "scroll",
                adjustActionsContainerMarginLeft,
            )
        }
    }, [containerRef.current, isRowHovered])

    const adjustActionsContainerMarginLeft = () => {
        if (!containerRef.current) {
            return
        }

        const boundaries = containerRef.current?.getBoundingClientRect()

        if (!boundaries) {
            return
        }

        setActionsContainerMarginLeft(boundaries.left * -1)
    }

    const onNavigateToPolicy = () => {
        if (props.ignoreNavigateToPolicy) {
            return
        }

        const neededUrl = PoliciesHelper.getSinglePolicyBaseUrl(
            props.listType,
        ).replace(":nirvanaRequestId", props.policy.coverageResult.resultId)

        navigate(neededUrl)
    }

    return (
        <div
            ref={containerRef}
            className={`
                ${props.className}
                flex group relative
            `}
            onMouseEnter={() => {
                setIsRowHovered(true)
                adjustActionsContainerMarginLeft()
            }}
            onMouseLeave={() => {
                setIsRowHovered(false)
            }}
        >
            <div className="table-row w-full">
                <div
                    className={`
                        min-h-[55px]
                        ${!props.ignoreHorizontalPadding ? "px-[16px]" : ""}
                        ${
                            !props.ignoreRowBorder
                                ? "border-b border-solid border-brand-warmLight"
                                : ""
                        }
                        ${!props.ignoreNavigateToPolicy ? "cursor-pointer" : ""}
                        ${
                            props.isExternallySelected
                                ? "bg-brand-lilacLight"
                                : `bg-transparent ${
                                      props.highlightOnHover
                                          ? "hover:bg-brand-whitePurple"
                                          : ""
                                  }`
                        }
                    `}
                    onClick={onNavigateToPolicy}
                >
                    <div className="flex items-stretch pr-[60px]">
                        {props.isSelectable && (
                            <div
                                className="mr-[16px] relative top-[16px] p-[12px] -m-[12px]"
                                onClick={e => {
                                    e.stopPropagation()
                                }}
                            >
                                <CheckboxElement
                                    name="select"
                                    defaultChecked={props.isExternallySelected}
                                    watchDefaultCheckedChange={true}
                                    onChange={newValue => {
                                        // Don't process if value didn't change
                                        if (
                                            props.isExternallySelected ===
                                            newValue
                                        ) {
                                            return
                                        }

                                        return newValue
                                            ? props.onSelected?.(props.policy)
                                            : props.onDeselectedItem?.(
                                                  props.policy,
                                              )
                                    }}
                                    disabled={props.isSelectionCheckboxDisabled}
                                />
                            </div>
                        )}

                        {props.columns.map((columnConfig, index) => {
                            if (!columnConfig.isEnabled) {
                                return null
                            }

                            const isCellHighlighted =
                                isOverridesEnabled &&
                                PoliciesHelper.isColumnCellHighlighted(
                                    props.policy,
                                    columnConfig,
                                )

                            const columnValue =
                                ExportReportHelper.getMappedColumnValue(
                                    props.policy,
                                    columnConfig,
                                )

                            const isColumnValueSet =
                                columnValue &&
                                columnValue !==
                                    ExportReportHelper.resolveEmptyColumnValue(
                                        columnConfig,
                                        props.policy,
                                        null,
                                    )

                            return (
                                <div
                                    key={index}
                                    style={{
                                        width: `${
                                            columnConfig.width ||
                                            POLICIES_COLUMNS_CONFIG.defaultColumnWidth
                                        }px`,
                                    }}
                                    className={`
                                        py-[16px] mr-[16px] flex-shrink-0
                                        ${
                                            isCellHighlighted
                                                ? "bg-brand-greenHighlight px-[16px] border-b-[2px] border-solid border-brand-primary"
                                                : ""
                                        }
                                    `}
                                >
                                    <div
                                        className={`
                                            px-[8px] pt-[3px] pb-[1px] -mx-[8px] -mt-[3px] -mb-[1px] inline-block
                                            ${isColumnValueSet ? "cursor-text" : ""}
                                        `}
                                        onClick={
                                            isColumnValueSet
                                                ? e => {
                                                      e.stopPropagation()
                                                  }
                                                : undefined
                                        }
                                    >
                                        <PrimaryText
                                            className={
                                                columnConfig.mouseflowIgnore
                                                    ? "mouseflow-ignore"
                                                    : ""
                                            }
                                        >
                                            {typeof columnValue === "string" ? (
                                                <div
                                                    className="text-ellipsis overflow-x-hidden"
                                                    style={{
                                                        width: columnConfig.width,
                                                    }}
                                                >
                                                    {columnValue}
                                                </div>
                                            ) : (
                                                columnValue
                                            )}
                                        </PrimaryText>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            {/*Keeping this block always to the right of row is tricky because we have horizontal scroll in table, but doable with JS*/}
            {/*we can't use position:fixed since position should be relative to row, not to window*/}
            {/*Check adjustActionsContainerMarginLeft above for details where it uses setActionsContainerMarginLeft*/}
            {/*it will update marginLeft on scroll and will keep element to the right*/}
            {!props.ignoreNavigateToPolicy && props.withRowActionsOverlay ? (
                <div
                    className={`
                        hidden group-hover:block absolute z-10
                        h-full w-[80px] bg-brand-lilacLight top-0
                        border-l border-solid border-brand-lilac
                    `}
                    style={{
                        left: "100vw",
                        transform: "translateX(-100%)",
                        marginLeft: `${actionsContainerMarginLeft}px`,
                    }}
                >
                    <div className="flex items-center h-full">
                        <div
                            className="flex-1 flex items-center justify-center cursor-pointer h-full relative"
                            onClick={onNavigateToPolicy}
                        >
                            <div className="w-full absolute screen-center">
                                <img
                                    className="mx-auto mt-[8px]"
                                    src={forwardIcon}
                                    alt="View"
                                />

                                <PrimaryText
                                    centered
                                    className="mt-[8px]"
                                    typography="caption"
                                >
                                    View
                                </PrimaryText>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

interface IPoliciesListRowComponentProps {
    className?: string
    elementRef?: React.RefObject<HTMLDivElement>
    listType: PoliciesListType
    policy: IOneVerifyCoverageCheckHistory
    columns: IPolicyColumnConfiguration[]
    highlightOnHover?: boolean
    ignoreNavigateToPolicy?: boolean
    isSelectable?: boolean
    isSelectionCheckboxDisabled?: boolean
    isExternallySelected?: boolean
    onSelected?: (item: IOneVerifyCoverageCheckHistory) => void
    onDeselectedItem?: (items: IOneVerifyCoverageCheckHistory) => void
    ignoreRowBorder?: boolean
    ignoreHorizontalPadding?: boolean
    withRowActionsOverlay?: boolean
}
